import React from 'react';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/SEOHead';
import { motion } from 'framer-motion';
import { FileText, CheckCircle, Shield, Scale, AlertTriangle, Power, RefreshCw, Mail } from 'lucide-react';

const TermsOfService = () => {
  const sections = [
    {
      icon: CheckCircle,
      title: '1. Acceptance of Terms',
      content: "By accessing or using NoQ5.ai's Salesforce AI Copilot services, you agree to be bound by these Terms of Service and our Privacy Policy. If you do not agree to these terms, please do not use our services.",
    },
    {
      icon: FileText,
      title: '2. Description of Service',
      content: 'NoQ5.ai provides an AI-powered copilot service that integrates with Salesforce to help sales representatives update their CRM automatically through voice and chat interfaces.',
    },
    {
      icon: Shield,
      title: '3. User Obligations',
      content: 'You agree to use our services responsibly and in accordance with all applicable laws. You are responsible for maintaining the security of your account credentials and for all activities that occur under your account.',
    },
    {
      icon: Shield,
      title: '4. Data and Privacy',
      content: 'Your use of our services is also governed by our Privacy Policy. We are committed to protecting your data and maintaining GDPR compliance. Your data is encrypted, never shared without consent, and always under your control.',
    },
    {
      icon: Scale,
      title: '5. Intellectual Property',
      content: 'The NoQ5.ai service, including all software, algorithms, and related materials, is the intellectual property of NoQ5.ai Technologies Ltd. You may not copy, modify, distribute, or create derivative works based on our services.',
    },
    {
      icon: AlertTriangle,
      title: '6. Limitation of Liability',
      content: 'NoQ5.ai Technologies Ltd. shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of our services, even if we have been advised of the possibility of such damages.',
    },
    {
      icon: Power,
      title: '7. Termination',
      content: 'We may suspend or terminate your access to our services at any time, with or without cause, with or without notice. You may also terminate your account at any time by contacting us.',
    },
    {
      icon: RefreshCw,
      title: '8. Changes to Terms',
      content: 'We reserve the right to modify these Terms of Service at any time. We will notify users of material changes via email or through our service. Continued use of our services after changes constitute acceptance of the new terms.',
    },
  ];

  return (
    <Layout>
      <SEOHead 
        title="Terms of Service - NoQ5.ai"
        description="Read our terms of service to understand the rules and regulations for using NoQ5.ai's Salesforce AI Copilot services."
        canonicalUrl="https://noq5.ai/terms-of-service"
      />
      <div className="container px-4 md:px-6 py-12 max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-8"
        >
          {/* Header */}
          <div className="text-center space-y-4 mb-12">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 mb-4"
            >
              <FileText className="w-8 h-8 text-primary" />
            </motion.div>
            <h1 className="text-4xl md:text-5xl font-bold text-foreground">Terms of Service</h1>
            <p className="text-muted-foreground">Last updated: September 17, 2025</p>
          </div>

          {/* Sections */}
          <div className="space-y-6">
            {sections.map((section, index) => (
              <motion.section
                key={section.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index }}
                className="bg-card border border-border rounded-2xl p-6 hover:border-primary/30 transition-colors"
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <section.icon className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold mb-3 text-foreground">{section.title}</h2>
                    <p className="text-muted-foreground leading-relaxed">{section.content}</p>
                  </div>
                </div>
              </motion.section>
            ))}

            {/* Contact Section */}
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9 }}
              className="bg-gradient-to-br from-primary/5 via-card to-primary/5 border border-primary/20 rounded-2xl p-6"
            >
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold mb-3 text-foreground">9. Contact Information</h2>
                  <p className="text-muted-foreground mb-4">
                    If you have any questions about these Terms of Service, please contact us:
                  </p>
                  <div className="bg-card border border-border p-4 rounded-xl">
                    <p className="font-semibold text-foreground">NoQ5.ai Technologies Ltd.</p>
                    <p className="text-muted-foreground">
                      Email:{' '}
                      <a href="mailto:hello@noq5.ai" className="text-primary hover:underline">
                        hello@noq5.ai
                      </a>
                    </p>
                    <p className="text-muted-foreground">Address: Dublin, Ireland</p>
                  </div>
                </div>
              </div>
            </motion.section>
          </div>
        </motion.div>
      </div>
    </Layout>
  );
};

export default TermsOfService;